// frontend/src/pages/Staff.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/UIComponents";

/**
 * Sales Staff Master Directory Maintenance Module (public.sys_user)
 * Left Panel: Debounced search, sales-only filter, and user cards
 * Right Panel: Profile and role toggle controller
 */
export default function Staff() {
  const nav = useNavigate();

  // ---------- Left: search + filter list tracking states ----------
  const [q, setQ] = useState("");
  const [salesOnly, setSalesOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");

  // ---------- Right: dynamic layout entry structure form states ----------
  const empty = {
    isEdit: false,
    user_id: "",
    user_name: "",
    crm_id: "",
    comp_name: "",
    comp_uname: "",
    ord_form_filename: "",
    is_sal: true, // Default newly created staff as active sales
    is_mgr: false,
    is_adm: false,
    is_bql: false,
    is_dir: false,
    is_kyt: false,
  };

  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hint, setHint] = useState("");
  const [errorSave, setErrorSave] = useState("");

  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 920 : false
  );

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const qRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => loadList(q, salesOnly), 250);
    return () => clearTimeout(t);
  }, [q, salesOnly]);

  useEffect(() => {
    loadList("", false);
    setTimeout(() => qRef.current?.focus(), 0);
  }, []);

  async function loadList(term, onlySales) {
    try {
      setLoadingList(true);
      setErrorList("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "staff");
      if (term && term.trim()) u.searchParams.set("q", term.trim());
      if (onlySales) u.searchParams.set("sales_only", "1");

      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setRows(Array.isArray(j.rows) ? j.rows : []);
    } catch (e) {
      setErrorList(String(e.message || e));
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }

  function selectRow(r) {
    const next = {
      isEdit: true,
      user_id: r.user_id,
      user_name: String(r.user_name || ""),
      crm_id: String(r.crm_id || ""),
      comp_name: String(r.comp_name || ""),
      comp_uname: String(r.comp_uname || ""),
      ord_form_filename: String(r.ord_form_filename || ""),
      is_sal: Boolean(r.is_sal),
      is_mgr: Boolean(r.is_mgr),
      is_adm: Boolean(r.is_adm),
      is_bql: Boolean(r.is_bql),
      is_dir: Boolean(r.is_dir),
      is_kyt: Boolean(r.is_kyt),
    };
    setForm(next);
    setInitialForm(next);
    setHint(`Staff profile #${r.user_id} selected.`);
    setErrorSave("");
  }

  function newRow() {
    const next = { ...empty, isEdit: false };
    setForm(next);
    setInitialForm(next);
    setHint("Ready for new staff registration.");
    setErrorSave("");
  }

  function resetForm() {
    setForm(initialForm);
    setHint("Form changes reverted back to initial values.");
    setErrorSave("");
  }

  const changed = JSON.stringify(form) !== JSON.stringify(initialForm);

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
  }

  async function onSave() {
    setErrorSave("");
    setHint("");

    if (!String(form.user_name || "").trim()) {
      return setErrorSave("Họ và tên nhân viên là bắt buộc / Staff user name is required.");
    }

    setSaving(true);
    try {
      let r;
      if (form.isEdit) {
        r = await fetch("/api/data?scope=staff&action=update", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      } else {
        r = await fetch("/api/data?scope=staff&action=create", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      }

      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP Transaction Error " + r.status);

      setHint(form.isEdit ? "Cập nhật hồ sơ thành công / Staff profile updated." : "Thêm mới nhân viên thành công / New staff added.");
      await loadList(q, salesOnly);
      if (!form.isEdit && j.row) {
        selectRow(j.row);
      }
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!form.isEdit || !form.user_id) return;
    const confirmMsg = `Bạn có chắc muốn xóa nhân viên "${form.user_name}" (ID: ${form.user_id})?\nAre you sure you want to remove this staff profile?`;
    if (!window.confirm(confirmMsg)) return;

    setDeleting(true);
    setErrorSave("");
    try {
      const r = await fetch("/api/data?scope=staff&action=delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: form.user_id }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP Transaction Error " + r.status);

      alert(j.message || "Xóa thành công.");
      await loadList(q, salesOnly);
      newRow();
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setDeleting(false);
    }
  }

  // ---------- Layout configurations ----------
  const grid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "380px 1fr",
    gap: isNarrow ? 10 : 20,
    alignItems: "start",
  };
  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "200px 1fr",
    gap: "12px",
    alignItems: "center",
  };
  const smallText = { fontSize: isNarrow ? 11 : 12, opacity: 0.75 };

  return (
    <div className="app-page" style={{ maxWidth: "1350px" }}>
      <main className="container">
        <h1 className="menu-title" style={isNarrow ? { fontSize: 22, lineHeight: "24px" } : undefined}>
          Tuiss Web Tools — Sales Management
        </h1>
        <h2 style={{ marginTop: -8, ...(isNarrow ? { fontSize: 16 } : {}) }}>
          Danh mục Nhân sự & Bán hàng / Staff Directory (public.sys_user)
        </h2>

        {/* Action Command Controls Bar Row */}
        <div style={{ margin: "8px 0 16px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <Button onClick={() => nav("/purchase/config")}>← Trở về / Back</Button>
          <Button onClick={newRow}>+ Thêm nhân viên / New Staff</Button>
          <Button onClick={resetForm} disabled={!changed}>Reset Form</Button>
          {form.isEdit && (
            <Button
              onClick={onDelete}
              disabled={deleting || saving}
              style={{
                backgroundColor: "#fee2e2",
                color: "#b91c1c",
                borderColor: "#f87171",
              }}
            >
              {deleting ? "Đang xử lý..." : "Xóa / Delete"}
            </Button>
          )}
          <Button
            onClick={onSave}
            disabled={saving || (!changed && form.isEdit)}
            style={{
              backgroundColor: "#2563eb",
              color: "#fff",
              borderColor: "#1d4ed8",
              opacity: saving || (!changed && form.isEdit) ? 0.5 : 1,
              cursor: saving || (!changed && form.isEdit) ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Đang lưu..." : "Lưu thay đổi / Save"}
          </Button>
          {hint ? <span style={{ marginLeft: 8, color: "#166534", fontWeight: 600, ...smallText }}>{hint}</span> : null}
        </div>

        <div style={grid}>
          {/* LEFT PANEL VIEW: DATABASE SEARCH INDEX CODES LIST */}
          <section className="box" style={{ overflow: "hidden" }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>
              Tìm kiếm nhân viên (Tên / CRM ID / Đơn vị)
            </label>
            <input
              ref={qRef}
              className="text-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nhập tên nhân viên hoặc mã CRM..."
            />

            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="checkbox"
                  checked={salesOnly}
                  onChange={(e) => setSalesOnly(e.target.checked)}
                />
                <b>Chỉ hiển thị Sales (is_sal = 1)</b>
              </label>
            </div>

            <div style={{ marginTop: 6, ...smallText, color: "#666" }}>
              {loadingList ? "Đang tải dữ liệu..." : `Tìm thấy: ${rows.length} nhân sự`}
              {errorList ? <span style={{ color: "crimson", marginLeft: 8 }}>{errorList}</span> : null}
            </div>

            <div style={{ marginTop: 10, maxHeight: "560px", overflowY: "auto", borderTop: "1px solid #eee" }}>
              {rows.map((r) => {
                const isSelectedRow = r.user_id === form.user_id;
                return (
                  <button
                    key={r.user_id}
                    onClick={() => selectRow(r)}
                    className="menu-card"
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      borderRadius: 0,
                      boxShadow: "none",
                      background: isSelectedRow ? "#eeffe6" : "transparent",
                      fontSize: "14px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700, color: "#111" }}>
                        #{r.user_id} — {r.user_name || "(Chưa có tên)"}
                      </span>
                      {Boolean(r.is_sal) ? (
                        <span style={{ background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: 4, fontSize: "11px", fontWeight: 700 }}>
                          SALES
                        </span>
                      ) : (
                        <span style={{ background: "#f3f4f6", color: "#6b7280", padding: "1px 6px", borderRadius: 4, fontSize: "11px" }}>
                          NON-SALES
                        </span>
                      )}
                    </div>
                    <div style={{ margin: "3px 0", color: "#555", fontSize: "12px" }}>
                      CRM ID: <b>{r.crm_id || "—"}</b> {r.comp_name ? `| Đơn vị: ${r.comp_name}` : ""}
                    </div>
                    <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                      {Boolean(r.is_mgr) && <span style={{ background: "#e0e7ff", color: "#3730a3", fontSize: "10px", padding: "1px 4px", borderRadius: 3 }}>Quản lý</span>}
                      {Boolean(r.is_adm) && <span style={{ background: "#fef3c7", color: "#92400e", fontSize: "10px", padding: "1px 4px", borderRadius: 3 }}>Admin</span>}
                      {Boolean(r.is_kyt) && <span style={{ background: "#f3e8ff", color: "#6b21a8", fontSize: "10px", padding: "1px 4px", borderRadius: 3 }}>Kỹ thuật</span>}
                    </div>
                  </button>
                );
              })}
              {rows.length === 0 && !loadingList && (
                <div style={{ padding: 12, ...smallText, textAlign: "center", color: "#888" }}>
                  Không tìm thấy nhân sự phù hợp.
                </div>
              )}
            </div>
          </section>

          {/* RIGHT PANEL VIEW: MODIFICATION DATA FIELDS SHEET */}
          <section className="box">
            <h3 style={{ margin: "0 0 16px 0", fontSize: "15px", borderBottom: "1px solid #eee", paddingBottom: "8px", fontWeight: 600, color: "#333" }}>
              {form.isEdit ? `Chỉnh sửa nhân viên: [ #${form.user_id} - ${form.user_name} ]` : "Thêm mới hồ sơ nhân sự (New Staff Profile)"}
            </h3>

            <div style={fieldGrid}>
              <label style={{ fontWeight: 600 }}>User ID (Mã hệ thống)</label>
              <input
                className="text-input"
                name="user_id"
                value={form.user_id || "Tự động tạo (Auto-generated)"}
                disabled
                style={{ backgroundColor: "#f3f4f6", color: "#6b7280" }}
              />

              <label style={{ fontWeight: 600 }}>Họ và tên / Full Name *</label>
              <input
                className="text-input"
                name="user_name"
                value={form.user_name}
                onChange={onChange}
                placeholder="e.g., Phùng Thị Thuỳ Vân"
              />

              <label style={{ fontWeight: 600 }}>CRM ID (Mã CRM)</label>
              <input
                className="text-input"
                name="crm_id"
                value={form.crm_id}
                onChange={onChange}
                placeholder="e.g., IHCM2304201635"
              />

              <label style={{ fontWeight: 600 }}>Công ty / Chi nhánh</label>
              <input
                className="text-input"
                name="comp_name"
                value={form.comp_name}
                onChange={onChange}
                placeholder="e.g., Tuiss Vietnam HCM / HN"
              />

              <label style={{ fontWeight: 600 }}>Tên máy tính / Username</label>
              <input
                className="text-input"
                name="comp_uname"
                value={form.comp_uname}
                onChange={onChange}
                placeholder="e.g., LAPTOP-JBEOT400"
              />

              <label style={{ fontWeight: 600 }}>File mẫu đơn hàng</label>
              <input
                className="text-input"
                name="ord_form_filename"
                value={form.ord_form_filename}
                onChange={onChange}
                placeholder="e.g., THAOMAIthaot"
              />

              {/* Roles & Permissions Checkbox Matrix */}
              <div style={{ gridColumn: "1 / -1", borderTop: "1px dashed #ccc", paddingTop: "12px", marginTop: "8px", fontWeight: "700", color: "#1e40af", fontSize: "13px" }}>
                🛡️ Phân quyền & Vai trò phòng ban (Roles & Flags)
              </div>

              <label style={{ fontWeight: 600 }}>Nhân viên Kinh doanh (Sales)</label>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    name="is_sal"
                    checked={Boolean(form.is_sal)}
                    onChange={onChange}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: "13px", fontWeight: 600, color: Boolean(form.is_sal) ? "#16a34a" : "#666" }}>
                    {Boolean(form.is_sal) ? "Đang là Sales (Hiển thị trong dropdown Cart)" : "Không phải Sales"}
                  </span>
                </label>
              </div>

              <label style={{ fontWeight: 600 }}>Quản lý (Manager)</label>
              <input
                type="checkbox"
                name="is_mgr"
                checked={form.is_mgr === 1}
                onChange={onChange}
                style={{ width: 18, height: 18, cursor: "pointer" }}
              />

              <label style={{ fontWeight: 600 }}>Quản trị hệ thống (Admin)</label>
              <input
                type="checkbox"
                name="is_adm"
                checked={form.is_adm === 1}
                onChange={onChange}
                style={{ width: 18, height: 18, cursor: "pointer" }}
              />

              <label style={{ fontWeight: 600 }}>Bộ phận Kỹ thuật</label>
              <input
                type="checkbox"
                name="is_kyt"
                checked={form.is_kyt === 1}
                onChange={onChange}
                style={{ width: 18, height: 18, cursor: "pointer" }}
              />

              <label style={{ fontWeight: 600 }}>Ban Giám đốc (Director)</label>
              <input
                type="checkbox"
                name="is_dir"
                checked={form.is_dir === 1}
                onChange={onChange}
                style={{ width: 18, height: 18, cursor: "pointer" }}
              />

              <label style={{ fontWeight: 600 }}>Ban Quản lý</label>
              <input
                type="checkbox"
                name="is_bql"
                checked={form.is_bql === 1}
                onChange={onChange}
                style={{ width: 18, height: 18, cursor: "pointer" }}
              />
            </div>

            {errorSave && (
              <div style={{ color: "crimson", marginTop: 14, fontWeight: 500, padding: "8px 12px", background: "#fef2f2", borderRadius: "6px", border: "1px solid #fca5a5" }}>
                {errorSave}
              </div>
            )}
            <div style={{ marginTop: 14, ...smallText, color: "#666", fontStyle: "italic" }}>
              Sau khi điền thông tin, nhấn <b>Lưu thay đổi</b> để ghi nhận dữ liệu vào bảng <code>public.sys_user</code>.
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}